import { OptionType } from '@evinced-private/ui-common';

import { apiCall } from 'src/common/services/api/ServerApi';
import { Logger } from 'src/common/services/Logger';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { TestRowSharedType } from '../helpers/types';
import { APP_TYPE, AppType } from '../shared-modal/constants';

import { addChanges, buildBasePayload, buildBatchPayload } from './builders/PayloadBuilder';
import { buildAllRowChanges } from './helpers/ChangeBuildingHelpers';
import {
	createAdditionFromChange,
	findRowsWithLabel,
	LabelChangeForUpdate,
	parseLabelChange,
	validateLabelChange
} from './helpers/LabelChangeHelpers';
import { buildPayloadsForRows } from './helpers/PayloadBuildingHelpers';
import { transformAdditionsForRow, transformRemovals } from './transformers/LabelTransformers';
import { BatchUpdatePayload, RequestPayload } from './types/PayloadTypes';

const getApiEndpoint = (appType?: AppType): string =>
	appType === APP_TYPE.WFA
		? `${process.env.API_ENDPOINT}/update/wfa/batch`
		: `${process.env.API_ENDPOINT}/update/wsdk/batch`;

const buildUpdatePayloads = (
	testRows: TestRowSharedType[],
	rowIdToAdditionsMap: Map<string, Array<{ labelType: OptionType; value: OptionType }>>,
	configuredLabels?: ConfigurationLabel[]
): RequestPayload[] => {
	return buildPayloadsForRows(testRows, rowIdToAdditionsMap, (row, rowAdditions) =>
		buildAllRowChanges(row, rowAdditions, configuredLabels)
	);
};

export const addLabels = async (
	testRows: TestRowSharedType[],
	additions: Array<{ labelType: OptionType; value: OptionType }>,
	configuredLabels?: ConfigurationLabel[],
	appType?: AppType
): Promise<void> => {
	try {
		if (!testRows.length || !additions.length) {
			return;
		}

		const updates = testRows
			.map((row: TestRowSharedType) => {
				const runId = row?.runId || row?.sessionId;
				if (!runId) {
					return null;
				}

				const changes = transformAdditionsForRow(row, additions, configuredLabels);
				if (!changes.length) {
					return null;
				}

				const payload = buildBasePayload(runId);
				return addChanges(payload, changes);
			})
			.filter((payload): payload is RequestPayload => payload !== null);

		if (!updates.length) {
			return;
		}

		await apiCall('POST', getApiEndpoint(appType), {
			data: { updates },
			isAbsoluteUrl: true
		});
	} catch (error) {
		Logger.error('Error adding labels to tests:', error);
		throw error;
	}
};

export const removeLabels = async (
	testRows: TestRowSharedType[],
	removals: string[],
	appType?: AppType
): Promise<void> => {
	try {
		if (!testRows.length || !removals.length) {
			return;
		}

		const changes = transformRemovals(removals);

		if (!changes.length) {
			return;
		}

		const filteredRunSessionIds = testRows
			.filter((row) => {
				const rowLabels = row.labels ?? [];
				return removals.some((removalString) => rowLabels.includes(removalString));
			})
			.map((row) => row.runId || row.sessionId)
			.filter((runSessionId): runSessionId is string => runSessionId !== undefined);

		if (!filteredRunSessionIds.length) {
			return;
		}

		const payload = buildBatchPayload(filteredRunSessionIds, changes);

		await apiCall('POST', getApiEndpoint(appType), {
			data: payload,
			isAbsoluteUrl: true
		});
	} catch (error) {
		Logger.error('Error removing labels from tests:', error);
		throw error;
	}
};

const buildRowIdToAdditionsMap = (
	testRows: TestRowSharedType[],
	changes: LabelChangeForUpdate[],
	configuredLabels?: ConfigurationLabel[]
): Map<string, Array<{ labelType: OptionType; value: OptionType }>> => {
	const rowIdToAdditionsMap = new Map<
		string,
		Array<{ labelType: OptionType; value: OptionType }>
	>();

	for (const change of changes) {
		if (!validateLabelChange(change, configuredLabels)) {
			continue;
		}

		const parsed = parseLabelChange(change, configuredLabels);
		if (!parsed) {
			continue;
		}

		const rowIds = findRowsWithLabel(testRows, change.oldLabelValue, configuredLabels);
		if (rowIds.size === 0) {
			continue;
		}

		const addition = createAdditionFromChange(parsed.oldParsed, parsed.newParsed);

		for (const rowId of rowIds) {
			if (!rowIdToAdditionsMap.has(rowId)) {
				rowIdToAdditionsMap.set(rowId, []);
			}
			const additions = rowIdToAdditionsMap.get(rowId);
			if (additions) {
				additions.push(addition);
			}
		}
	}

	return rowIdToAdditionsMap;
};

export const updateLabels = async (
	testRows: TestRowSharedType[],
	changes: LabelChangeForUpdate[],
	configuredLabels?: ConfigurationLabel[],
	appType?: AppType
): Promise<void> => {
	try {
		if (!testRows.length || !changes.length) {
			return;
		}

		const rowIdToAdditionsMap = buildRowIdToAdditionsMap(testRows, changes, configuredLabels);

		if (rowIdToAdditionsMap.size === 0) {
			return;
		}

		const updates = buildUpdatePayloads(testRows, rowIdToAdditionsMap, configuredLabels);

		if (updates.length === 0) {
			return;
		}

		const batchPayload: BatchUpdatePayload = { updates };

		await apiCall('POST', getApiEndpoint(appType), {
			data: batchPayload,
			isAbsoluteUrl: true
		});
	} catch (error) {
		Logger.error('Error updating labels on tests:', error);
		throw error;
	}
};
